import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../../utils/fetchJson';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export default function HelpCenterPage() {
  const { t, i18n } = useTranslation();
  const [topics, setTopics] = useState([]);
  const [query, setQuery] = useState('');

  const tagLabel = (tag) => t(`support.helpCenter.tags.${tag}`, { defaultValue: tag });

  useEffect(() => {
    let isMounted = true;
    fetchJson('/support/help-center')
      .then((data) => { if (isMounted) setTopics(data?.topics || []); })
      .catch(() => { /* silently fall back to default topics — do not surface network errors on the public page */ });
    return () => { isMounted = false; };
  }, [t]);

  const defaultTopics = useMemo(() => ([
    {
      id: 'nav-basics',
      title: t('support.helpCenter.defaultTopics.navBasics.title'),
      tags: ['getting-started', 'navigation', 'roles'],
      content: t('support.helpCenter.defaultTopics.navBasics.content')
    },
    {
      id: 'phases',
      title: t('support.helpCenter.defaultTopics.phases.title'),
      tags: ['phases', 'academics'],
      content: t('support.helpCenter.defaultTopics.phases.content')
    },
    {
      id: 'permissions',
      title: t('support.helpCenter.defaultTopics.permissions.title'),
      tags: ['permissions', 'access', 'security'],
      content: t('support.helpCenter.defaultTopics.permissions.content')
    },
    {
      id: 'users-staff',
      title: t('support.helpCenter.defaultTopics.usersStaff.title'),
      tags: ['users', 'staff'],
      content: t('support.helpCenter.defaultTopics.usersStaff.content')
    },
    {
      id: 'students-admissions',
      title: t('support.helpCenter.defaultTopics.studentsAdmissions.title'),
      tags: ['students', 'admissions'],
      content: t('support.helpCenter.defaultTopics.studentsAdmissions.content')
    },
    {
      id: 'attendance',
      title: t('support.helpCenter.defaultTopics.attendance.title'),
      tags: ['attendance', 'teachers'],
      content: t('support.helpCenter.defaultTopics.attendance.content')
    },
    {
      id: 'timetable',
      title: t('support.helpCenter.defaultTopics.timetable.title'),
      tags: ['timetable', 'academics'],
      content: t('support.helpCenter.defaultTopics.timetable.content')
    },
    {
      id: 'evaluations-marks',
      title: t('support.helpCenter.defaultTopics.evaluationsMarks.title'),
      tags: ['evaluations', 'marks', 'report-cards'],
      content: t('support.helpCenter.defaultTopics.evaluationsMarks.content')
    },
    {
      id: 'fees-payments',
      title: t('support.helpCenter.defaultTopics.feesPayments.title'),
      tags: ['finance', 'fees', 'payments'],
      content: t('support.helpCenter.defaultTopics.feesPayments.content')
    },
    {
      id: 'payroll-overview',
      title: t('support.helpCenter.defaultTopics.payrollOverview.title'),
      tags: ['finance', 'salary', 'payroll'],
      content: t('support.helpCenter.defaultTopics.payrollOverview.content')
    },
    {
      id: 'reports',
      title: t('support.helpCenter.defaultTopics.reports.title'),
      tags: ['reports', 'exports'],
      content: t('support.helpCenter.defaultTopics.reports.content')
    },
    {
      id: 'support-tickets',
      title: t('support.helpCenter.defaultTopics.supportTickets.title'),
      tags: ['support', 'tickets'],
      content: t('support.helpCenter.defaultTopics.supportTickets.content')
    }
  ]), [i18n.language, t]);

  const mergedTopics = useMemo(() => {
    const apiTopics = Array.isArray(topics) ? topics : [];
    const normalizedApi = apiTopics
      .map((x, idx) => ({
        id: x?.id || `api-${idx}`,
        title: x?.title || t('support.helpCenter.fallback.topicTitle'),
        content: x?.content || '',
        tags: Array.isArray(x?.tags) ? x.tags : []
      }))
      .filter((x) => x.title || x.content);

    const base = defaultTopics;
    const combined = [...normalizedApi, ...base];

    const seen = new Set();
    return combined.filter((x) => {
      const k = String(x.id);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [defaultTopics, i18n.language, t, topics]);

  const filteredTopics = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return mergedTopics;
    return mergedTopics.filter((x) => {
      const tagLabels = (x.tags || []).map(tagLabel);
      const hay = `${x.title || ''}\n${x.content || ''}\n${(x.tags || []).join(' ')}\n${tagLabels.join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  }, [mergedTopics, query, i18n.language, t]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" gutterBottom>{t('support.helpCenter.title')}</Typography>
      <Stack spacing={2} sx={{ maxWidth: 1100 }}>
        <TextField
          label={t('support.helpCenter.searchLabel')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          fullWidth
        />

        <Divider />

        {filteredTopics.length === 0 ? (
          <Alert severity="info">{t('support.helpCenter.noResults')}</Alert>
        ) : (
          filteredTopics.map((topic, idx) => (
            <Accordion
              key={topic.id || idx}
              disableGutters
              sx={{
                backgroundColor: 'transparent',
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: 'transparent',
                  '& .MuiAccordionSummary-content': { my: 1 }
                }}
              >
                <Stack spacing={1} sx={{ width: '100%' }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {topic.title}
                  </Typography>
                  {Array.isArray(topic.tags) && topic.tags.length > 0 && (
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                      {topic.tags.slice(0, 6).map((tag) => (
                        <Chip key={`${topic.id}-${tag}`} size="small" label={tagLabel(tag)} />
                      ))}
                    </Stack>
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {topic.content}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Stack>
    </Box>
  );
}
